import { Patient } from '@principle-theorem/principle-core';
import {
  type IBasePatient,
  type INote,
  type IPatient,
  type IsPrimaryContact,
  PatientStatus,
  type WithPrimaryContact,
} from '@principle-theorem/principle-core/interfaces';
import {
  type Timestamp,
  type WithRef,
  isObject,
  isSameRef,
  serialise,
  toMoment,
} from '@principle-theorem/shared';
import * as stringify from 'json-stable-stringify';
import {
  assignWith,
  get,
  isArray,
  isNil,
  isPlainObject,
  isString,
  omit,
  uniqWith,
} from 'lodash';

type PatientKey = keyof (IBasePatient & IsPrimaryContact & WithPrimaryContact);

/**
 * Principle and later migration steps own these fields, so a migration never
 * overwrites them on a patient that already exists.
 */
export const DESTINATION_OWNED_PATIENT_KEYS: PatientKey[] = [
  'metadata',
  'accountSummary',
  'profileImageURL',
  'relationships',
  'isPrimaryContact',
  'primaryContact',
  'deleted',
];

const separatelyMergedKeys: PatientKey[] = [
  'status',
  'notes',
  'tags',
  'medicareCard',
  'healthFundCard',
  'dvaCard',
];

/**
 * Deceased and blocked are deliberate flags, so they win from either
 * migration. A patient active at one practice and inactive at another has most
 * likely just stopped going to the second one, so active beats inactive.
 */
const statusPriority: Record<PatientStatus, number> = {
  [PatientStatus.Deceased]: 4,
  [PatientStatus.Blocked]: 3,
  [PatientStatus.Active]: 2,
  [PatientStatus.Inactive]: 1,
  [PatientStatus.Lead]: 0,
};

/**
 * Merges a migration's patient onto a patient that already exists, either one
 * shared with another migration or one this migration wrote on an earlier run.
 * A value the migration doesn't have never unsets the existing one, and that
 * includes the defaults `Patient.init` fills in. Where both have a value, the
 * last migration to run wins.
 */
export class MigratedPatient {
  static mergeOnto(
    existing: WithRef<IPatient>,
    incoming: IPatient,
    incomingCreatedAt?: Timestamp
  ): WithRef<IPatient> {
    const patientDefaults = Patient.init({});
    const merged = assignWith(
      { ...existing },
      omit(incoming, [
        ...DESTINATION_OWNED_PATIENT_KEYS,
        ...separatelyMergedKeys,
      ]),
      (existingValue: unknown, incomingValue: unknown, key?: string) =>
        MigratedPatient._preferIncoming(
          existingValue,
          incomingValue,
          key ? get(patientDefaults, key) : undefined
        )
    );

    return {
      ...merged,
      status: MigratedPatient._higherStatus(existing.status, incoming.status),
      notes: MigratedPatient._combineNotes(existing.notes, incoming.notes),
      tags: uniqWith([...existing.tags, ...incoming.tags], (tag, otherTag) =>
        isSameRef(tag, otherTag)
      ),
      medicareCard: MigratedPatient._cardWithNumber(
        existing.medicareCard,
        incoming.medicareCard,
        (card) => card.number
      ),
      healthFundCard: MigratedPatient._cardWithNumber(
        existing.healthFundCard,
        incoming.healthFundCard,
        (card) => card.membershipNumber
      ),
      dvaCard: MigratedPatient._cardWithNumber(
        existing.dvaCard,
        incoming.dvaCard,
        (card) => card.number
      ),
      createdAt: MigratedPatient._earlierCreatedAt(
        existing.createdAt,
        incomingCreatedAt
      ),
    };
  }

  private static _preferIncoming(
    existingValue: unknown,
    incomingValue: unknown,
    defaultValue: unknown
  ): unknown {
    const isSupplied =
      MigratedPatient._hasValue(incomingValue) &&
      (defaultValue === undefined ||
        !MigratedPatient._isSameValue(incomingValue, defaultValue));
    return isSupplied || existingValue === undefined
      ? incomingValue
      : existingValue;
  }

  private static _hasValue(value: unknown): boolean {
    if (isString(value)) {
      return value.trim().length > 0;
    }
    if (isArray(value)) {
      return value.length > 0;
    }
    if (isPlainObject(value) && isObject(value)) {
      return Object.values(value).some((child) =>
        MigratedPatient._hasValue(child)
      );
    }
    return !isNil(value);
  }

  /**
   * Compares the serialised forms, because migration data read back from
   * storage has lost the `undefined` fields an in-memory value still has.
   */
  private static _isSameValue(value: unknown, otherValue: unknown): boolean {
    return stringify(serialise(value)) === stringify(serialise(otherValue));
  }

  /**
   * Notes get a new uid every time a migration builds them, so a note already
   * on the patient is recognised by its content.
   */
  private static _combineNotes(
    existingNotes: INote[],
    incomingNotes: INote[]
  ): INote[] {
    const newNotes = incomingNotes.filter(
      (incomingNote) =>
        !existingNotes.some((existingNote) =>
          MigratedPatient._isSameValue(
            existingNote.content,
            incomingNote.content
          )
        )
    );
    return [...existingNotes, ...newNotes];
  }

  private static _cardWithNumber<Card>(
    existingCard: Card | undefined,
    incomingCard: Card | undefined,
    cardNumber: (card: Card) => string
  ): Card | undefined {
    return incomingCard && MigratedPatient._hasValue(cardNumber(incomingCard))
      ? incomingCard
      : existingCard;
  }

  private static _higherStatus(
    existingStatus: PatientStatus,
    incomingStatus: PatientStatus
  ): PatientStatus {
    return statusPriority[incomingStatus] > statusPriority[existingStatus]
      ? incomingStatus
      : existingStatus;
  }

  private static _earlierCreatedAt(
    existingCreatedAt: Timestamp,
    incomingCreatedAt?: Timestamp
  ): Timestamp {
    return incomingCreatedAt &&
      toMoment(incomingCreatedAt).isBefore(toMoment(existingCreatedAt))
      ? incomingCreatedAt
      : existingCreatedAt;
  }
}
